<template>
  <div @keydown.esc.capture="open = false">
    <div :class="{ 'tw-mt-1': $slots.link }">
      <slot name="link" :openModal="openModal" />
    </div>
    <FocusLock :disabled="!open">
      <transition
        enter-class="tw-opacity-0"
        enter-active-class="tw-transition-all tw-duration-300"
        leave-active-class="tw-transition-all tw-duration-300"
        leave-to-class="tw-opacity-0"
      >
        <div
          v-if="open"
          class="modal-wrapper tw-flex tw-items-center tw-z-150 tw-fixed tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-black tw-bg-opacity-25"
          @click.self="open = false"
          @touchstart="onTouchStart"
          @touchmove="onTouchMove"
          ref="scrollable"
        >
          <div
            class="modal-content tw-relative tw-block tw-mx-auto tw-bg-white tw-p-4 tw-rounded-2xl"
            :class="{
              'modal-content--big': bigmodal,
              'modal-content--fit': fitContent,
              'modal-content--autoheight': autoheight,
              'tw-overflow-y-auto': !autoheight
            }"
            ref="modalContent"
          >
            <button
              @click="open = false"
              @mousedown.prevent
              class="modal-close tw-text-2xl tw-absolute tw-top-0 tw-flex tw-items-center tw-justify-center tw-mt-1/2 tw-mr-1/2 tw-p-1 tw-right-0 tw-rounded-full"
              :class="{
                'tw-w-5 tw-h-5': !compact,
                'tw-w-4 tw-h-4': compact
              }"
              @focus="onFocusClose"
            >
              <span
                class="tw-block tw-absolute tw-top-1/2 tw-transform tw--translate-y-1/2 tw-icon tw-icon-kreuz tw-text-red-500"
              />
            </button>

            <slot :closeModal="closeModal" :open="open" />
          </div>
        </div>
      </transition>
    </FocusLock>
  </div>
</template>

<script>
import FocusLock from 'vue-focus-lock';

export default {
  components: {
    FocusLock
  },
  props: {
    compact: Boolean,
    bigmodal: Boolean,
    fitContent: Boolean,
    autoheight: Boolean
  },
  data() {
    return {
      open: false,
      firstFocus: true,
      resizeObserver: null,
      resizeTimeout: null,
      touchStartY: 0,
      isIOS: false,
      triggerElement: null
    };
  },
  mounted() {
    // Detect iOS devices
    this.isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  },
  methods: {
    openModal(triggerElement = null) {
      this.triggerElement = triggerElement || document.activeElement;
      this.open = true;
    },
    closeModal() {
      this.open = false;
    },
    onFocusClose($event) {
      if (this.firstFocus) {
        $event.target.blur();
      }
      this.firstFocus = false;
    },
    handleAutoHeight() {
      if (!this.autoheight || !this.$refs.modalContent) return;

      const modalContent = this.$refs.modalContent;
      const scrollableElement = modalContent.querySelector(
        '.modal-content-scrollable'
      );

      const descriptionElement = modalContent.querySelector(
        '.modal-content-description'
      );
      if (scrollableElement) {
        const viewHeight = window.innerHeight;
        const maxModalHeight = viewHeight - 50;
        const modalHeight = modalContent.scrollHeight;

        if (modalHeight > maxModalHeight) {
          const descriptionHeight = descriptionElement
            ? descriptionElement.offsetHeight
            : 0;
          scrollableElement.style.height = `${viewHeight -
            descriptionHeight -
            150}px`;
        }
      }
    },
    onTouchStart(e) {
      if (!this.isIOS) return;
      this.touchStartY = e.touches[0].clientY;
    },
    onTouchMove(e) {
      if (!this.isIOS || !this.touchStartY) return;
      const touchY = e.touches[0].clientY;
      const moveY = this.touchStartY - touchY;
      this.touchStartY = touchY;

      let target = e.target;
      while (target && target !== this.$refs.scrollable) {
        if (target.scrollHeight > target.clientHeight) {
          target.scrollTop += moveY;
          if (e.cancelable && e.preventDefault) e.preventDefault();
          return;
        }
        target = target.parentElement;
      }
    }
  },
  watch: {
    open(newValue) {
      if (newValue === true) {
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        this.$nextTick(() => {
          this.$root.$emit('openModal', this.$refs.scrollable);
          this.handleAutoHeight();

          if (this.autoheight) {
            this.resizeObserver = new ResizeObserver(() => {
              if (this.resizeTimeout) {
                clearTimeout(this.resizeTimeout);
              }
              this.resizeTimeout = setTimeout(() => {
                requestAnimationFrame(() => {
                  this.handleAutoHeight();
                });
              }, 100);
            });
            if (this.$refs.modalContent) {
              this.resizeObserver.observe(this.$refs.modalContent);
            }
          }
        });
      } else {
        setTimeout(() => {
          document.body.style.overflow = '';
          document.body.style.position = '';
          document.body.style.width = '';
        }, 0);

        this.firstFocus = true;
        this.$root.$emit('closeModal', this.$refs.scrollable);

        // Restore focus to trigger element
        if (this.triggerElement) {
          this.$nextTick(() => {
            this.triggerElement.focus();
            this.triggerElement = null;
          });
        }

        if (this.resizeTimeout) {
          clearTimeout(this.resizeTimeout);
          this.resizeTimeout = null;
        }

        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }
      }
    }
  },
  beforeDestroy() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../sass/mixins';

.modal-content {
  max-width: px-to-rem(760px);
  width: 90%;
  max-height: 90%;

  &--big {
    max-width: px-to-rem(1024px);
  }

  &--fit {
    max-width: fit-content;
  }

  &--autoheight {
    max-height: calc(100% - 40px);
  }
}

.modal-wrapper {
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
}

.modal-close:hover {
  @apply tw-border tw-border-red-600;

  span {
    @apply tw-text-red-600;
  }
}

.modal-close:focus-visible {
  @apply tw-border tw-border-blue-500;

  span {
    @apply tw-text-red-500;
  }
}
</style>
